// CalendarPage — global unified calendar (/app/calendar). Internal users get a
// birds-eye view across clients with client + project filters; portal users are
// auto-scoped to their own client (read + their portal-visible events).
import { useEffect, useMemo, useState } from "react";
import { CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import UnifiedCalendar from "@/components/calendar/UnifiedCalendar";

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant"]);

export default function CalendarPage() {
    const { user } = useAuth();
    const { clients, portalClientId, isInternalView } = useView();
    const [clientId, setClientId] = useState("");
    const [projectId, setProjectId] = useState("");
    const [projects, setProjects] = useState([]);

    // Portal users are locked to their own client.
    const effectiveClientId = isInternalView ? (clientId || null) : portalClientId;

    useEffect(() => {
        if (!effectiveClientId) { setProjects([]); return; }
        api.get("/projects", { params: { client_id: effectiveClientId } })
            .then((r) => setProjects(r.data)).catch(() => setProjects([]));
    }, [effectiveClientId]);

    const canEdit = CAN_EDIT_ROLES.has(user?.role);

    const clientOptions = useMemo(() => clients || [], [clients]);

    return (
        <div className="space-y-5" data-testid="calendar-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F1F1F] flex items-center gap-2">
                        <CalendarDays className="text-[var(--brand-accent,#C75A1B)]" size={24} /> Calendar
                    </h1>
                    <p className="text-[13px] text-[#6F6A64] mt-0.5">Every scheduled date across the workspace, in one view.</p>
                </div>
                <div className="flex items-center gap-2">
                    {isInternalView && (
                        <select data-testid="calendar-client-select" value={clientId}
                            onChange={(e) => { setClientId(e.target.value); setProjectId(""); }}
                            className="h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm bg-white">
                            <option value="">All clients</option>
                            {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    )}
                    <select data-testid="calendar-project-select" value={projectId}
                        onChange={(e) => setProjectId(e.target.value)} disabled={!effectiveClientId}
                        className="h-9 px-3 rounded-lg border border-[#E5E0DA] text-sm bg-white disabled:opacity-50">
                        <option value="">All projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                </div>
            </div>

            <UnifiedCalendar
                key={`${effectiveClientId || "all"}-${projectId || "all"}`}
                clientId={effectiveClientId}
                projectId={projectId || null}
                canEdit={canEdit}
                role={user?.role}
                showScopeNote
            />
        </div>
    );
}
